import React, { useEffect, useMemo, useState } from "react";
import { connect, useSelector } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
    Layout,
    Breadcrumb,
    Button,
    ConfigProvider,
    Row,
    Col,
    Card,
    Select,
    Divider,
    Space,
    Popconfirm,
    message,
} from "antd";
import { useNavigate } from "react-router-dom";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";
import ptBRIntl from "antd/lib/locale/pt_BR";
import { ProTable } from "@ant-design/pro-components";
import {
    BellOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined,
    MailOutlined, SyncOutlined, UserAddOutlined,
} from "@ant-design/icons";

import { userApi } from "../../../services/actions/users";
import {
    addUsersToAlerts,
    getAlertsByUnit,
    removeUserFromAlerts,
    updateAlertsLevel
} from "../../../auth/actions/automationsActions";

const { Option } = Select;

function AlertsAutomation() {
    const navigate = useNavigate();
    const { company } = useSelector((state) => state.user);

    const [options, setOptions] = useState([]); // [{label, value, name}]
    const [selectedTagsEmail, setSelectedTagsEmail] = useState([]);
    const [selectedTagsNotification, setSelectedTagsNotification] = useState([]);
    const [emailLevel, setEmailLevel] = useState("all");
    const [notificationLevel, setNotificationLevel] = useState("all");
    const [usersEmail, setUsersEmail] = useState([]);           // [{userId, name}]
    const [usersNotification, setUsersNotification] = useState([]); // [{userId, name}]

    useEffect(() => {
        (async () => {
            try {
                const cfg = await getAlertsByUnit(company.unitId);
                if (cfg?.config) {
                    setEmailLevel(cfg.config.email?.level ?? "all");
                    setNotificationLevel(cfg.config.notification?.level ?? "all");
                    setUsersEmail(cfg.config.email?.users ?? []);
                    setUsersNotification(cfg.config.notification?.users ?? []);
                }

                // Opções para selects
                const { data } = await userApi.getUsersByUnit(company.unitId, company.companyId);
                setOptions(
                    (data || []).map((u) => ({
                        label: `${u.firstName} ${u.lastName} (${u.email})`, // mostrado no Select
                        value: u.uuid,
                        name: `${u.firstName} ${u.lastName}`,               // nome puro
                    }))
                );
            } catch (e) {
                console.error(e);
                message.error("Falha ao carregar configurações/usuários.");
            }
        })();
    }, [company.unitId, company.companyId]);

    const handleSelectChange = (values, type) => {
        if (type === "email") setSelectedTagsEmail(values);
        else setSelectedTagsNotification(values);
    };

    const handleLevelChange = async (level, channel) => {
        try {
            if (channel === "email") setEmailLevel(level);
            else setNotificationLevel(level);
            const updated = await updateAlertsLevel({ unitId: company.unitId, channel, level });
            setEmailLevel(updated?.config?.email?.level ?? level);
            setNotificationLevel(updated?.config?.notification?.level ?? notificationLevel);
            message.success("Nível atualizado.");
        } catch {
            message.error("Erro ao atualizar o nível.");
        }
    };

    const addSelectedToList = async (channel) => {
        try {
            if (channel === "email") {
                const toAdd = options
                    .filter(o => selectedTagsEmail.includes(o.value))
                    .map(o => ({ userId: o.value, label: o.name }));
                const updated = await addUsersToAlerts({ unitId: company.unitId, channel: "email", users: toAdd });
                setUsersEmail(updated?.config?.email?.users ?? []);
                setSelectedTagsEmail([]);
            } else {
                const toAdd = options
                    .filter(o => selectedTagsNotification.includes(o.value))
                    .map(o => ({ userId: o.value, label: o.name }));
                const updated = await addUsersToAlerts({ unitId: company.unitId, channel: "notification", users: toAdd });
                setUsersNotification(updated?.config?.notification?.users ?? []);
                setSelectedTagsNotification([]);
            }
            message.success("Usuários adicionados.");
        } catch {
            message.error("Erro ao adicionar usuários.");
        }
    };

    const removeFromList = async (userId, channel) => {
        try {
            await removeUserFromAlerts({ unitId: company.unitId, channel, userId });

            if (channel === "email") setUsersEmail(prev => prev.filter(u => u.userId !== userId));
            else setUsersNotification(prev => prev.filter(u => u.userId !== userId));

            message.success("Removido com sucesso.");
        } catch {
            message.error("Erro ao remover.");
        }
    };

    const columnsEmail = useMemo(
        () => [
            { title: "Nome", dataIndex: "label" },
            {
                title: "Ações",
                key: "actions",
                width: 140,
                render: (_, record) => (
                    <Popconfirm
                        title="Remover destinatário de e-mail?"
                        onConfirm={() => removeFromList(record.userId, "email")}
                        okText="Remover"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                            Remover
                        </Button>
                    </Popconfirm>
                ),
            },
        ],
        [usersEmail]
    );

    const columnsNotify = useMemo(
        () => [
            { title: "Usuário", dataIndex: "label" },
            {
                title: "Ações",
                key: "actions",
                width: 140,
                render: (_, record) => (
                    <Popconfirm
                        title="Remover do canal de notificação?"
                        onConfirm={() => removeFromList(record.userId, "notification")}
                        okText="Remover"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                            Remover
                        </Button>
                    </Popconfirm>
                ),
            },
        ],
        [usersNotification]
    );

    const routes = [
        { breadcrumbName: "Automações", href: "/automacoes" },
        { breadcrumbName: "Configuração de Alertas", href: `/automacoes/alertas` },
    ];

    return (
        <Layout>
            <LayoutTopSide>
                <TopBar />
                <Layout>
                    <DrawerSider />
                    <Layout style={{ padding: "0 24px 24px", minHeight: "100vh" }}>
                        <PageHeader
                            style={{ margin: "16px 0 5px 0" }}
                            title="Configuração de alertas"
                            subTitle="Defina quem recebe notificações na plataforma e por e-mail sobre as execuções das automações."
                            onBack={() => window.history.back()}
                            breadcrumb={
                                <Breadcrumb>
                                    {routes.map((a) =>
                                        a.href ? (
                                            <Breadcrumb.Item key={a.href}>
                                                <a onClick={() => navigate(a.href)}>{a.breadcrumbName}</a>
                                            </Breadcrumb.Item>
                                        ) : (
                                            <Breadcrumb.Item key={a.breadcrumbName}>{a.breadcrumbName}</Breadcrumb.Item>
                                        )
                                    )}
                                </Breadcrumb>
                            }
                        />

                        <div style={{ width: "100%", height: "100%", background: "var(--cp-surface)" }}>
                            <ConfigProvider locale={ptBRIntl}>
                                <Row gutter={24} style={{ paddingTop: "10px" }}>
                                    {/* Plataforma */}
                                    <Col span={24} md={12}>
                                        <Card
                                            title={
                                                <Row gutter={16} align="middle">
                                                    <Col flex="auto">
                                                        <Space>
                                                            <BellOutlined />
                                                            <span>Notificações na Plataforma</span>
                                                        </Space>
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <Select
                                                            style={{ width: "100%" }}
                                                            value={notificationLevel}
                                                            onChange={(v) => handleLevelChange(v, "notification")}
                                                        >
                                                            <Option value="all"><Space><SyncOutlined /> Sempre</Space></Option>
                                                            <Option value="success"><Space><CheckCircleOutlined /> Apenas em sucesso</Space></Option>
                                                            <Option value="error"><Space><CloseCircleOutlined /> Apenas em falhas</Space></Option>
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            }
                                        >
                                            <Card>
                                                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                                                    <Row gutter={16} align="middle">
                                                        <Col flex="auto">
                                                            <Select
                                                                showSearch
                                                                filterOption={(input, option) =>
                                                                    (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                                                }
                                                                mode="multiple"
                                                                style={{ width: "100%" }}
                                                                value={selectedTagsNotification}
                                                                onChange={(values) => handleSelectChange(values, "notification")}
                                                                placeholder="Selecionar usuários"
                                                                options={options}
                                                            />
                                                        </Col>
                                                        <Col>
                                                            <Space>
                                                                <Button
                                                                    type="primary"
                                                                    icon={<UserAddOutlined />}
                                                                    onClick={() => addSelectedToList("notification")}
                                                                    disabled={!selectedTagsNotification.length}
                                                                >
                                                                    Adicionar
                                                                </Button>
                                                                <Button type="default" ghost onClick={() => setSelectedTagsNotification([])}>
                                                                    Limpar seleção
                                                                </Button>
                                                            </Space>
                                                        </Col>
                                                    </Row>

                                                    <Divider />

                                                    <ProTable
                                                        rowKey={(r) => r.userId}
                                                        search={false}
                                                        pagination={false}
                                                        columns={columnsNotify}
                                                        dataSource={usersNotification}
                                                        toolBarRender={false}
                                                        bordered
                                                    />
                                                </Space>
                                            </Card>
                                        </Card>
                                    </Col>

                                    {/* E-mail */}
                                    <Col span={24} md={12}>
                                        <Card
                                            title={
                                                <Row gutter={16} align="middle">
                                                    <Col flex="auto">
                                                        <Space>
                                                            <MailOutlined />
                                                            <span>Notificações por E-mail</span>
                                                        </Space>
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <Select
                                                            style={{ width: "100%" }}
                                                            value={emailLevel}
                                                            onChange={(v) => handleLevelChange(v, "email")}
                                                        >
                                                            <Option value="all"><Space><SyncOutlined /> Sempre</Space></Option>
                                                            <Option value="success"><Space><CheckCircleOutlined /> Apenas em sucesso</Space></Option>
                                                            <Option value="error"><Space><CloseCircleOutlined /> Apenas em falhas</Space></Option>
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            }
                                        >
                                            <Card>
                                                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                                                    <Row gutter={16} align="middle">
                                                        <Col flex="auto">
                                                            <Select
                                                                showSearch
                                                                filterOption={(input, option) =>
                                                                    (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                                                }
                                                                mode="multiple"
                                                                style={{ width: "100%" }}
                                                                value={selectedTagsEmail}
                                                                onChange={(values) => handleSelectChange(values, "email")}
                                                                placeholder="Selecionar usuários"
                                                                options={options}
                                                            />
                                                        </Col>
                                                        <Col>
                                                            <Space>
                                                                <Button
                                                                    type="primary"
                                                                    icon={<UserAddOutlined />}
                                                                    onClick={() => addSelectedToList("email")}
                                                                    disabled={!selectedTagsEmail.length}
                                                                >
                                                                    Adicionar
                                                                </Button>
                                                                <Button type="default" ghost onClick={() => setSelectedTagsEmail([])}>
                                                                    Limpar seleção
                                                                </Button>
                                                            </Space>
                                                        </Col>
                                                    </Row>

                                                    <Divider />

                                                    <ProTable
                                                        rowKey={(r) => r.userId}
                                                        search={false}
                                                        pagination={false}
                                                        columns={columnsEmail}
                                                        dataSource={usersEmail}
                                                        toolBarRender={false}
                                                        bordered
                                                    />
                                                </Space>
                                            </Card>
                                        </Card>
                                    </Col>
                                </Row>
                            </ConfigProvider>
                        </div>
                    </Layout>
                </Layout>
            </LayoutTopSide>
        </Layout>
    );
}

const mapStateToProps = ({ automation }) => ({ automation });
export default connect(mapStateToProps)(AlertsAutomation);
